import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import adminService from "../../api/services/adminService";
import uploadService from "../../api/services/uploadService";
import csrOutreachService from "../../api/services/csrOutreachService";
import { notifyApprovalsPendingChanged } from "../../utils/approvalsEvents";
import { uploadPaymentProofFiles } from "../../utils/paymentProofUrls";
import PaymentProofAttachments from "../../components/SubscriptionHistory/PaymentProofAttachments";

const CURRENCIES = ["PKR", "AED", "USD", "EUR", "GBP"];
const inputCls =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

/**
 * CSR buy-premium: mandatory payment proof → team lead verification.
 */
export default function CsrPremiumProofModal({ customer, onClose, onSubmitted }) {
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("PKR");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [notes, setNotes] = useState("");
  const [proofItems, setProofItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const name =
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim() ||
    customer?.email ||
    "Customer";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminService.getSubscriptionPlans();
        if (!cancelled && res?.plans?.length) {
          setPlans(res.plans);
          setSubscriptionId(String(res.plans[0].id));
        }
      } catch {
        if (!cancelled) toast.error("Failed to load subscription plans");
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      proofItems.forEach((it) => {
        if (it?.preview) URL.revokeObjectURL(it.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke leftover blobs on unmount only
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer?.assignmentId) return;
    const amt = parseFloat(amount);
    if (!amount || Number.isNaN(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const planId = subscriptionId ? parseInt(subscriptionId, 10) : null;
    if (!planId) {
      toast.error("Select a subscription plan");
      return;
    }
    if (!proofItems.length) {
      toast.error("Payment proof image is required");
      return;
    }

    setSubmitting(true);
    try {
      const { url: payment_proof_url } = await uploadPaymentProofFiles(
        proofItems.map((it) => it.file),
        uploadService.single
      );
      if (!payment_proof_url) {
        toast.error("Failed to upload payment proof");
        return;
      }
      await csrOutreachService.createPremiumRequest(customer.assignmentId, {
        amount: amt,
        currency,
        subscription_plan_id: planId,
        payment_proof_url,
        notes: notes.trim() || undefined,
      });
      toast.success("Payment proof sent to team lead for verification");
      notifyApprovalsPendingChanged({ delta: 1 });
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (customer?.isPremium) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4">
          <h3 className="text-lg font-bold text-gray-900">Already premium</h3>
          <p className="text-sm text-gray-600">
            <strong>{name}</strong> already has an active premium subscription. Buy premium is not
            required.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: "#FB565F" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Proof of payment</h3>
          <p className="text-sm text-gray-500 mt-1">
            Upload one or more payment proof images for <strong>{name}</strong>. Team lead must
            verify before buy premium is marked.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Amount *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputCls}
              placeholder="e.g. 2500"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Currency *</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Plan *</label>
              <select
                value={subscriptionId}
                onChange={(e) => setSubscriptionId(e.target.value)}
                className={inputCls}
                disabled={loadingPlans}
                required
              >
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || `Plan ${p.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <PaymentProofAttachments
            items={proofItems}
            onChange={setProofItems}
            label="Payment proof attachments *"
            hint="Upload 1–8 screenshots or photos of the payment (required)."
          />
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${inputCls} min-h-[70px]`}
              placeholder="Bank transfer reference, screenshot details…"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || loadingPlans}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: "#FB565F" }}
            >
              {submitting ? "Submitting…" : "Submit for verification"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
